import { useContext, useState, useEffect, useCallback } from 'react';
import { useParams, RouterContext, goTo } from 't-lib-ui/router';
import MainLayout from '../../components/MainLayout';
import AdminHeader from '../../components/admin/AdminHeader';
import { FlowForm, FlowFormLeaveContext, ButtonBar } from 't-lib-ui/structure';
import { AppState } from '../../context/general';
import { authenticatedRequest, getAdminMagicSecret } from '../../connector/auth';
import { nonNull } from 't-lib-ui/helpers';
import { formatCompactDate } from '../../utils/formatting';
import { A, TextButton, Spinner } from 't-lib-ui/widgets';
import { chevronBackIcon, buildIcon, chevronForwardIcon } from '../../components/icons';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import AdminSecretModal from '../../components/admin/AdminSecretModal';

interface PostSummary {
  number: number;
  title?: string;
  link?: string;
  pubDate?: string;
}

interface RepairResult {
  originalPostCount: number;
  newPostCount: number;
  duplicatesRemoved: number;
}

const PAGE_SIZE = 1000;

const AdminRepairFeedPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [feedTitle, setFeedTitle] = useState('');
  const [postCount, setPostCount] = useState(0);
  const [posts, setPosts] = useState<PostSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [error, setError] = useState('');
  const [pageStart, setPageStart] = useState(0);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState<RepairResult | null>(null);

  const magic = getAdminMagicSecret() || adminSecret || '';

  const loadFeed = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await authenticatedRequest<any>(
        nonNull(appState.state.authData), 'GET', 'api',
        `/api/admin/feed/${id}`, false, { magic }
      );
      setFeedTitle(result.title || result.shortTitle || 'Untitled');
      setPostCount(result.postCount || 0);
    } catch (e) {
      setError('Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, [id, appState.state.authData, magic]);

  const loadPosts = useCallback(async (start: number) => {
    try {
      setLoadingPosts(true);
      setError('');
      const result = await authenticatedRequest<{ posts: PostSummary[] }>(
        nonNull(appState.state.authData), 'GET', 'api',
        `/api/admin/feed/${id}/posts`, false,
        { magic, startIndex: String(start), count: String(PAGE_SIZE) }
      );
      setPosts(result.posts);
      setPageStart(start);
    } catch (e) {
      setError('Failed to load posts');
    } finally {
      setLoadingPosts(false);
    }
  }, [id, appState.state.authData, magic]);

  useEffect(() => {
    if (!magic) return;
    loadFeed().then(() => loadPosts(0));
  }, [magic, loadFeed, loadPosts]);

  const handleRepair = async () => {
    if (repairing) return;
    try {
      setRepairing(true);
      setError('');
      const result = await authenticatedRequest<RepairResult>(
        nonNull(appState.state.authData), 'POST', 'api',
        `/api/admin/feed/${id}/repair?magic=${encodeURIComponent(magic)}`, false, undefined, 300_000
      );
      setRepairResult(result);
      setPostCount(result.newPostCount);
      // Reload posts after repair
      await loadPosts(0);
    } catch (e: any) {
      setError(`Repair failed: ${e.message || e}`);
    } finally {
      setRepairing(false);
    }
  };

  const handlePrev = () => {
    const newStart = Math.max(0, pageStart - PAGE_SIZE);
    loadPosts(newStart);
  };

  const handleNext = () => {
    const newStart = pageStart + PAGE_SIZE;
    if (newStart < postCount) loadPosts(newStart);
  };

  const pageEnd = Math.min(pageStart + PAGE_SIZE, postCount);
  const hasPrev = pageStart > 0;
  const hasNext = pageStart + PAGE_SIZE < postCount;

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
          <MainLayout
            selectedFeed="admin-feeds"
            right={({ onExpand }) => (
              <FlowForm main={true} className='full-margin'>
                <div style={{ padding: '24px' }}>
                  <AdminHeader title={`Repair: ${feedTitle || id}`} onExpand={onExpand} />
                  <div style={{ color: 'var(--gray-400)', marginBottom: '24px', fontSize: '0.9rem' }}>
                    {postCount} posts total
                  </div>

                  <ButtonBar size="small">
                    <TextButton
                      label="Back to Feed"
                      icon={chevronBackIcon}
                      onClick={() => goTo(routerCtx, `/a/admin/feed/${id}`)}
                      focussable="flow"
                    />
                    <TextButton
                      label="Start Repair"
                      icon={buildIcon}
                      onClick={handleRepair}
                      focussable="flow"
                      type="major"
                      busy={repairing}
                    />
                  </ButtonBar>

                  {error && (
                    <div style={{ padding: '12px', marginBottom: '16px', background: '#f8d7da', color: '#721c24', borderRadius: '4px', border: '1px solid #f5c6cb' }}>
                      {error}
                    </div>
                  )}

                  {repairResult && (
                    <div style={{ padding: '16px', marginBottom: '16px', background: '#d4edda', color: '#155724', borderRadius: '4px', border: '1px solid #c3e6cb' }}>
                      <strong>Repair complete.</strong> {repairResult.originalPostCount} &rarr; {repairResult.newPostCount} posts ({repairResult.duplicatesRemoved} duplicates removed)
                    </div>
                  )}

                  {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--gray-500)' }}>
                      Loading...
                    </div>
                  ) : (
                    <>
                      {/* Pagination */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--gray-850)', marginBottom: '8px' }}>
                        <TextButton
                          label="Previous"
                          icon={chevronBackIcon}
                          onClick={handlePrev}
                          focussable="flow"
                          enabled={hasPrev ? 'enabled' : 'disabled'}
                          size="small"
                        />
                        <span style={{ color: 'var(--gray-350)', fontSize: '0.9rem' }}>
                          Posts {pageStart}&ndash;{pageEnd - 1} of {postCount}
                        </span>
                        <TextButton
                          label="Next"
                          icon={chevronForwardIcon}
                          onClick={handleNext}
                          focussable="flow"
                          enabled={hasNext ? 'enabled' : 'disabled'}
                          size="small"
                        />
                      </div>

                      {loadingPosts ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                          <Spinner spinning={true} />
                        </div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid var(--gray-850)', fontSize: '0.85rem' }}>
                          <thead>
                            <tr style={{ background: 'var(--gray-975)' }}>
                              <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid var(--gray-850)', width: '60px' }}>#</th>
                              <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid var(--gray-850)' }}>Title</th>
                              <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid var(--gray-850)', width: '140px' }}>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {posts.map(post => (
                              <tr key={post.number} style={{ borderBottom: '1px solid var(--gray-925)' }}>
                                <td style={{ padding: '6px 12px', color: 'var(--gray-500)' }}>{post.number}</td>
                                <td style={{ padding: '6px 12px' }}>
                                  {post.link
                                    ? <A href={post.link}>{post.title || '(no title)'}</A>
                                    : (post.title || '(no title)')
                                  }
                                </td>
                                <td style={{ padding: '6px 12px', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>
                                  {post.pubDate ? formatCompactDate(post.pubDate) : ''}
                                </td>
                              </tr>
                            ))}
                            {!posts.length && (
                              <tr>
                                <td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: 'var(--gray-500)' }}>
                                  No posts found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}

                      {/* Bottom pagination */}
                      {posts.length > 20 && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderTop: '1px solid var(--gray-850)', marginTop: '8px' }}>
                          <TextButton
                            label="Previous"
                            icon={chevronBackIcon}
                            onClick={handlePrev}
                            focussable="flow"
                            enabled={hasPrev ? 'enabled' : 'disabled'}
                            size="small"
                          />
                          <span style={{ color: 'var(--gray-350)', fontSize: '0.9rem' }}>
                            Posts {pageStart}&ndash;{pageEnd - 1} of {postCount}
                          </span>
                          <TextButton
                            label="Next"
                            icon={chevronForwardIcon}
                            onClick={handleNext}
                            focussable="flow"
                            enabled={hasNext ? 'enabled' : 'disabled'}
                            size="small"
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </FlowForm>
            )}
            startWithLeftCollapsed={false}
          />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminRepairFeedPage;
